import { audio, prose, outlines } from '../storage/db';

let loggedNoEstimate = false;

export type StoreBreakdown = { count: number; bytes: number };

export type StorageStats = {
  estimateUsage?: number;
  estimateQuota?: number;
  audio: StoreBreakdown;
  prose: StoreBreakdown;
  outlines: StoreBreakdown;
  localStorageBytes: number;
};

async function sumBlobStore(
  getKeys: () => Promise<IDBValidKey[]>,
  getVal: (key: string) => Promise<Blob | undefined>
): Promise<StoreBreakdown> {
  const ks = await getKeys();
  let bytes = 0;
  let count = 0;
  for (const k of ks) {
    const v = await getVal(k as string);
    if (v) {
      bytes += v.size;
      count++;
    }
  }
  return { count, bytes };
}

async function sumStringStore(
  getKeys: () => Promise<IDBValidKey[]>,
  getVal: (key: string) => Promise<string | undefined>
): Promise<StoreBreakdown> {
  const ks = await getKeys();
  let bytes = 0;
  let count = 0;
  const enc = new TextEncoder();
  for (const k of ks) {
    const v = await getVal(k as string);
    if (v != null) {
      bytes += enc.encode(v).byteLength;
      count++;
    }
  }
  return { count, bytes };
}

function localStorageBytes(): number {
  let bytes = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const v = localStorage.getItem(k) ?? '';
      bytes += k.length + v.length;
    }
    return bytes * 2; // UTF-16 in-memory representation
  } catch {
    return 0;
  }
}

export async function computeStorageStats(): Promise<StorageStats> {
  let estimateUsage: number | undefined;
  let estimateQuota: number | undefined;
  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      const e = await navigator.storage.estimate();
      estimateUsage = e.usage;
      estimateQuota = e.quota;
      console.info(
        'storage.estimate:',
        JSON.stringify({ usage: e.usage, quota: e.quota })
      );
    } catch (err) {
      console.warn('storage.estimate failed', err);
    }
  } else if (!loggedNoEstimate) {
    loggedNoEstimate = true;
    console.info('storage.estimate not available on this browser');
  }

  const [audioBd, proseBd, outlineBd] = await Promise.all([
    sumBlobStore(audio.keys, audio.get),
    sumStringStore(prose.keys, prose.get),
    sumStringStore(outlines.keys, outlines.get),
  ]);

  return {
    estimateUsage,
    estimateQuota,
    audio: audioBd,
    prose: proseBd,
    outlines: outlineBd,
    localStorageBytes: localStorageBytes(),
  };
}
